import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { User, Building2, Award, Shield, Calendar, Clock, Save, Eye, EyeOff, Share2, Copy, Users, DollarSign, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${""}/api`;

const BADGE_STYLES = {
  gold: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', bar: 'bg-amber-500', icon: 'text-amber-500' },
  silver: { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-600', bar: 'bg-slate-500', icon: 'text-slate-500' },
  bronze: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', bar: 'bg-orange-500', icon: 'text-orange-500' },
  starter: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', bar: 'bg-teal-500', icon: 'text-teal-500' },
};

const FoundingPractitionerProfile = ({ email }) => {
  const [contrib, setContrib] = useState(null);
  useEffect(() => {
    if (!email) return;
    axios.get(`${API}/practitioner/contributions?email=${encodeURIComponent(email)}`)
      .then(r => { if (r.data?.success) setContrib(r.data.contributions); })
      .catch(() => {});
  }, [email]);

  if (!contrib) return null;
  const badge = contrib.badge || 'starter';
  const style = BADGE_STYLES[badge];
  const total = contrib.total_contributions || 0;
  const nextTier = badge === 'starter' ? 5 : badge === 'bronze' ? 20 : badge === 'silver' ? 50 : 100;
  const progress = Math.min((total / nextTier) * 100, 100);

  return (
    <Card className={`${style.bg} ${style.border} border`} data-testid="profile-founding-practitioner">
      <CardContent className="p-5">
        <div className="flex items-center gap-4 mb-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${style.border} ${style.bg}`}>
            <Award className={`w-6 h-6 ${style.icon}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`font-bold ${style.text}`}>{contrib.badge_label || 'Founding Practitioner'}</h3>
              <Badge variant="secondary" className={`text-[10px] ${style.bg} ${style.text}`}>{badge.toUpperCase()}</Badge>
            </div>
            <p className="text-xs text-slate-500">Early adopter building peptide medicine's future</p>
          </div>
        </div>
        <div className="mb-2">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{total} total contributions</span>
            <span>{nextTier - total > 0 ? `${nextTier - total} to next tier` : 'Max tier reached!'}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        <div className="grid grid-cols-4 gap-3 text-center mt-3">
          <div className="bg-white rounded-lg p-2 border border-slate-100">
            <div className="text-lg font-bold text-slate-800">{contrib.feedback_count || 0}</div>
            <div className="text-[10px] text-slate-500">Feedback</div>
          </div>
          <div className="bg-white rounded-lg p-2 border border-slate-100">
            <div className="text-lg font-bold text-slate-800">{contrib.protocols_generated || 0}</div>
            <div className="text-[10px] text-slate-500">Protocols</div>
          </div>
          <div className="bg-white rounded-lg p-2 border border-slate-100">
            <div className="text-lg font-bold text-slate-800">{contrib.community_posts || 0}</div>
            <div className="text-[10px] text-slate-500">Posts</div>
          </div>
          <div className="bg-white rounded-lg p-2 border border-slate-100">
            <div className="text-lg font-bold text-slate-800">{contrib.community_replies || 0}</div>
            <div className="text-[10px] text-slate-500">Replies</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AmbassadorDashboard = ({ email }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [signing, setSigning] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementText, setAgreementText] = useState('');
  const [signerName, setSignerName] = useState('');
  const [chosenDiscount, setChosenDiscount] = useState(0);

  const getToken = () => {
    try { return JSON.parse(localStorage.getItem('pp_auth') || '{}').token; } catch { return ''; }
  };
  const authHeaders = { headers: { Authorization: `Bearer ${getToken()}` } };

  useEffect(() => {
    if (!email) return;
    // Load dashboard & status in parallel
    axios.get(`${API}/referrals/dashboard`, authHeaders)
      .then(r => { if (r.data?.success) setData(r.data); })
      .catch(() => {});
    axios.get(`${API}/referrals/status`, authHeaders)
      .then(r => { if (r.data?.success) setStatus(r.data); })
      .catch(() => {});
  }, [email]);

  const openAgreement = async () => {
    try {
      const res = await axios.get(`${API}/referrals/agreement`);
      if (res.data?.success) {
        setAgreementText(res.data.agreement_text);
        setShowAgreement(true);
      }
    } catch { toast.error('Failed to load agreement'); }
  };

  const signAgreement = async () => {
    if (!signerName.trim()) { toast.error('Please enter your full name'); return; }
    setSigning(true);
    try {
      const res = await axios.post(`${API}/referrals/sign-agreement`, {
        full_name: signerName, discount_offered: chosenDiscount / 100,
      }, authHeaders);
      if (res.data?.success) {
        toast.success(`Ambassador agreement signed! Your code: ${res.data.referral_code}`);
        setShowAgreement(false);
        // Reload
        const d = await axios.get(`${API}/referrals/dashboard`, authHeaders);
        if (d.data?.success) setData(d.data);
        const s = await axios.get(`${API}/referrals/status`, authHeaders);
        if (s.data?.success) setStatus(s.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign agreement');
    } finally { setSigning(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(data?.referral_link || `https://peptideprotocols.ai/signup?ref=${data?.referral_code}`);
    toast.success('Referral link copied!');
  };

  const tierStyles = {
    founding_physician: { color: 'text-amber-700 bg-amber-50 border-amber-200' },
    contributing_practitioner: { color: 'text-teal-700 bg-teal-50 border-teal-200' },
    distributor: { color: 'text-blue-700 bg-blue-50 border-blue-200' },
    influencer: { color: 'text-purple-700 bg-purple-50 border-purple-200' },
  };

  const tierStyle = tierStyles[data?.tier || status?.tier] || tierStyles.influencer;

  return (
    <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-white" data-testid="ambassador-dashboard">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Share2 className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Ambassador Program</h3>
            <p className="text-xs text-slate-500">Earn commissions by referring practitioners</p>
          </div>
        </div>

        {/* Agreement signing modal */}
        {showAgreement && (
          <div className="bg-white rounded-lg border border-indigo-200 p-4 mb-4 space-y-3">
            <h4 className="font-semibold text-sm text-slate-800">Ambassador Agreement</h4>
            <pre className="text-xs text-slate-600 whitespace-pre-wrap max-h-48 overflow-y-auto bg-slate-50 p-3 rounded border">{agreementText}</pre>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-medium text-slate-600">Your full legal name (digital signature)</label>
                <input type="text" value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Dr. Jane Smith"
                  className="w-full px-3 py-2 border rounded text-sm mt-1" data-testid="agreement-signer-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Discount to offer referrals (comes from your commission): {chosenDiscount}%</label>
                <input type="range" min="0" max={((status?.max_discount || 0.15) * 100)} value={chosenDiscount}
                  onChange={e => setChosenDiscount(parseInt(e.target.value))} className="w-full mt-1" />
                <p className="text-[10px] text-slate-400">Max for your tier: {((status?.max_discount || 0.15) * 100).toFixed(0)}%</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={signAgreement} disabled={signing} className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm" data-testid="sign-agreement-btn">
                {signing ? 'Signing...' : 'I Agree & Sign'}
              </Button>
              <Button variant="outline" onClick={() => setShowAgreement(false)} className="text-sm">Cancel</Button>
            </div>
          </div>
        )}

        {!data?.has_code ? (
          <div className="text-center py-4">
            <p className="text-sm text-slate-600 mb-2">Join the Ambassador Program and earn commissions on every practitioner you refer.</p>
            {status?.all_tiers && (
              <div className="grid grid-cols-2 gap-2 mb-4 text-left">
                {Object.entries(status.all_tiers).map(([k, v]) => (
                  <div key={k} className={`text-xs p-2 rounded border ${tierStyles[k]?.color || ''}`}>
                    <div className="font-semibold">{v.label}</div>
                    <div>{k === 'contributing_practitioner' ? '30' : (v.commission_rate * 100).toFixed(0)}% commission</div>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={openAgreement} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="generate-referral-btn">
              Sign Agreement & Get My Link
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Badge className={`${tierStyle.color} border`}>
                {data.tier_label || data.tier} — Up to {data.tier === 'founding_physician' || data.tier === 'Founding Physician' ? '49' : data.tier === 'contributing_practitioner' || data.tier === 'Contributing Practitioner' ? '30' : ((data.commission_rate || 0) * 100).toFixed(0)}% Commission
              </Badge>
              <span className="text-xs text-slate-500">Code: <span className="font-mono font-bold text-indigo-600">{data.referral_code}</span></span>
            </div>

            {data.discount_offered > 0 && (
              <p className="text-xs text-slate-500">Offering {(data.discount_offered * 100).toFixed(0)}% discount to referrals (from your commission)</p>
            )}

            <div className="flex gap-2">
              <input type="text" readOnly value={data.referral_link || ''} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white text-slate-600 font-mono" data-testid="referral-link-input" />
              <Button variant="outline" size="sm" onClick={copyLink} className="flex-shrink-0" data-testid="copy-referral-link-btn">
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: Users, label: 'Total Referrals', value: data.total_referrals || 0, color: 'text-indigo-500' },
                { icon: TrendingUp, label: 'Active', value: data.active_referrals || 0, color: 'text-emerald-500' },
                { icon: DollarSign, label: 'Monthly', value: `$${(data.monthly_commission || 0).toFixed(2)}`, color: 'text-amber-500' },
                { icon: DollarSign, label: 'Lifetime', value: `$${(data.total_earned || 0).toFixed(2)}`, color: 'text-teal-500' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-lg p-3 border border-slate-100 text-center">
                  <s.icon className={`h-4 w-4 ${s.color} mx-auto mb-1`} />
                  <div className="text-lg font-bold text-slate-800">{s.value}</div>
                  <div className="text-[10px] text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>

            {!data.stripe_connect_id && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                <strong>Payout Setup:</strong> Connect your Stripe account for automated monthly payouts (min $50 threshold, paid on the 15th).
                <Button variant="outline" size="sm" className="ml-2 text-xs h-6 border-amber-300" data-testid="connect-stripe-btn"
                  onClick={() => toast.info('Stripe Connect will be available when production keys are configured.')}>
                  Connect Stripe
                </Button>
              </div>
            )}

            {data.referrals && data.referrals.length > 0 && (
              <div className="border-t border-slate-100 pt-3">
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Your Referrals</h4>
                <div className="space-y-1.5">
                  {data.referrals.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-white rounded px-3 py-2 border border-slate-100">
                      <span className="text-slate-600 font-mono">{r.email}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant={r.status === 'active' ? 'default' : 'secondary'} className="text-[10px]">{r.status}</Badge>
                        {r.monthly_commission > 0 && <span className="text-emerald-600 font-medium">${r.monthly_commission.toFixed(2)}/mo</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ProfilePage = ({ currentUser, onUserUpdate, setCurrentView }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setForm({
        full_name: currentUser.full_name || '',
        practice_name: currentUser.practice_name || '',
        npi_number: currentUser.npi_number || '',
        specialty: currentUser.specialty || '',
        credentials: currentUser.credentials || '',
      });
    }
  }, [currentUser]);

  const getToken = () => {
    try { return JSON.parse(localStorage.getItem('pp_auth') || '{}').token; } catch { return ''; }
  };

  const handleSaveProfile = async () => {
    if (!form.full_name || !form.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/auth/profile`, form, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.data.success) {
        onUserUpdate?.(res.data.user);
        setEditing(false);
        toast.success('Profile updated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Password changed');
      setPasswordForm({ old_password: '', new_password: '', confirm: '' });
      setShowPasswordSection(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password change failed');
    } finally { setSaving(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('pp_auth');
    localStorage.removeItem('beta_access');
    window.location.reload();
  };

  if (!currentUser) return null;

  const tierColors = {
    pre_release: 'bg-teal-100 text-teal-700',
    free_trial: 'bg-teal-100 text-teal-700',
    starter: 'bg-blue-100 text-blue-700',
    professional: 'bg-purple-100 text-purple-700',
    advanced: 'bg-amber-100 text-amber-700',
    // 2026-06-23 audit: Contributing Practitioners now get their own subscription_tier
    // (see auth_service.register_user). Dedicated color so the role is visually distinct.
    contributing: 'bg-emerald-100 text-emerald-700',
    admin: 'bg-rose-100 text-rose-700',
  };

  const tierLabels = { pre_release: 'Pre-Release Access', free_trial: 'Pre-Release Access', starter: 'Starter', professional: 'Professional', advanced: 'Advanced', admin: 'Admin', contributing: 'Contributing Practitioner' };
  const tierLabel = tierLabels[currentUser.subscription_tier] || (currentUser.subscription_tier || 'Pre-Release Access').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
  const trialEnd = currentUser.trial_end ? new Date(currentUser.trial_end) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  const inputCls = "w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none";

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 overflow-x-hidden" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">My Profile</h1>
          <p className="text-sm text-slate-500">Manage your account and practice details</p>
        </div>
        <Button variant="outline" onClick={handleLogout} className="text-red-600 border-red-200 hover:bg-red-50" style={{ height: '44px' }} data-testid="logout-btn">
          Sign Out
        </Button>
      </div>

      {/* Subscription Card */}
      <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-white">
        <CardContent className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Shield className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800">Subscription</span>
                <Badge className={tierColors[currentUser.subscription_tier] || tierColors.pre_release}>
                  {tierLabel}
                </Badge>
              </div>
              {(currentUser.subscription_tier === 'pre_release' || currentUser.subscription_tier === 'free_trial') && (
                <p className="text-xs text-teal-600 mt-0.5 flex items-center gap-1">
                  Pre-Release Access — 50% off locked in when billing activates
                </p>
              )}
            </div>
          </div>
          {(currentUser.subscription_tier === 'pre_release' || currentUser.subscription_tier === 'free_trial') && (
            <Badge className="bg-teal-100 text-teal-700">Pre-Release</Badge>
          )}
          {currentUser.subscription_tier && currentUser.subscription_tier !== 'free_trial' && (
            <Button size="sm" variant="outline" data-testid="manage-subscription-btn"
              onClick={async () => {
                try {
                  const token = getToken();
                  const res = await axios.post(`${API}/billing/portal-session`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  if (res.data.success && res.data.url) {
                    window.location.href = res.data.url;
                  } else {
                    toast.error(res.data.error || 'Unable to open subscription portal');
                  }
                } catch (err) {
                  toast.error('Unable to open subscription portal');
                }
              }}>
              Manage Subscription
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Founding Practitioner Program */}
      <FoundingPractitionerProfile email={currentUser?.email} />

      {/* Ambassador / Referral Program */}
      <AmbassadorDashboard email={currentUser?.email} />

      {/* Profile Card */}
      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <User className="h-5 w-5 text-teal-600" /> Personal Info
            </h2>
            {!editing ? (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-profile-btn">Edit</Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" onClick={handleSaveProfile} disabled={saving} className="bg-teal-600 text-white hover:bg-teal-700" data-testid="save-profile-btn">
                  <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Full Name</label>
              {editing ? (
                <input type="text" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className={inputCls} data-testid="profile-name" />
              ) : (
                <p className="text-sm font-medium text-slate-800 mt-1">{currentUser.full_name}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Email</label>
              <p className="text-sm text-slate-800 mt-1 break-all">{currentUser.email || 'Not set'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Credentials</label>
              {editing ? (
                <select value={form.credentials} onChange={e => setForm({...form, credentials: e.target.value})} className={inputCls}>
                  <option value="">Select</option>
                  {['MD','DO','NP','PA','ND','DC','PhD','Other'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <p className="text-sm text-slate-800 mt-1">{currentUser.credentials || 'Not set'}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Specialty</label>
              {editing ? (
                <select value={form.specialty} onChange={e => setForm({...form, specialty: e.target.value})} className={inputCls}>
                  <option value="">Select</option>
                  {['Functional Medicine','Integrative Medicine','Anti-Aging Medicine','Sports Medicine','Endocrinology','Internal Medicine','Naturopathic Medicine','Other'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <p className="text-sm text-slate-800 mt-1">{currentUser.specialty || 'Not set'}</p>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
              <Building2 className="h-4 w-4 text-teal-600" /> Practice Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Practice Name</label>
                {editing ? (
                  <input type="text" value={form.practice_name} onChange={e => setForm({...form, practice_name: e.target.value})} className={inputCls} />
                ) : (
                  <p className="text-sm text-slate-800 mt-1">{currentUser.practice_name || 'Not set'}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">NPI Number</label>
                {editing ? (
                  <input type="text" value={form.npi_number} onChange={e => setForm({...form, npi_number: e.target.value})} className={inputCls} />
                ) : (
                  <p className="text-sm text-slate-800 mt-1">{currentUser.npi_number || 'Not set'}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">License Number</label>
                {editing ? (
                  <input type="text" value={form.license_number || ''} onChange={e => setForm({...form, license_number: e.target.value})} className={inputCls} placeholder="e.g., MD-123456" />
                ) : (
                  <p className="text-sm text-slate-800 mt-1">{currentUser.license_number || 'Not set'}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Board Certifications</label>
                {editing ? (
                  <input type="text" value={form.board_certifications || ''} onChange={e => setForm({...form, board_certifications: e.target.value})} className={inputCls} placeholder="e.g., ABFM, ABIM" />
                ) : (
                  <p className="text-sm text-slate-800 mt-1">{currentUser.board_certifications || 'Not set'}</p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Practice Website</label>
                {editing ? (
                  <input type="url" value={form.practice_website || ''} onChange={e => setForm({...form, practice_website: e.target.value})} className={inputCls} placeholder="https://www.yourpractice.com" />
                ) : (
                  <p className="text-sm text-slate-800 mt-1">{currentUser.practice_website ? <a href={currentUser.practice_website} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">{currentUser.practice_website}</a> : 'Not set'}</p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-teal-600" /> Account
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs text-slate-500">
              <div><span className="block font-medium text-slate-700">Joined</span>{currentUser.created_at ? new Date(currentUser.created_at).toLocaleDateString() : 'N/A'}</div>
              <div><span className="block font-medium text-slate-700">Last Login</span>{currentUser.last_login ? new Date(currentUser.last_login).toLocaleDateString() : 'N/A'}</div>
              <div><span className="block font-medium text-slate-700">Protocols Generated</span>{currentUser.protocols_generated || 0}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card>
        <CardContent className="p-6">
          {!showPasswordSection ? (
            <Button variant="outline" onClick={() => setShowPasswordSection(true)} data-testid="change-password-btn">
              Change Password
            </Button>
          ) : (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800">Change Password</h3>
              <div className="grid gap-3 max-w-sm">
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={passwordForm.old_password}
                    onChange={e => setPasswordForm({...passwordForm, old_password: e.target.value})}
                    className={inputCls + ' pr-10'} placeholder="Current password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <input type="password" value={passwordForm.new_password}
                  onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  className={inputCls} placeholder="New password (min 8 chars)" />
                <input type="password" value={passwordForm.confirm}
                  onChange={e => setPasswordForm({...passwordForm, confirm: e.target.value})}
                  className={inputCls} placeholder="Confirm new password" />
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setShowPasswordSection(false)}>Cancel</Button>
                <Button onClick={handleChangePassword} disabled={saving} className="bg-teal-600 text-white hover:bg-teal-700" data-testid="save-password-btn">
                  {saving ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
